import { FC, forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import cs from 'classnames';
import { refactorSelection } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import { showInvalidFieldMsg } from '@/common/utils/ways';
import { getStandardTaskSelection, postDirectTaskUpdate } from '@/common/api/expandStore/expansiontask';
import { getCircleTaskDetail } from '@/common/api/expandStore/expansionCircleTask';
import styles from './index.module.less';
import { Col, Form, Modal, Row } from 'antd';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import V2Form from '@/common/components/Form/V2Form';
import V2FormInput from '@/common/components/Form/V2FormInput/V2FormInput';
import V2FormSelect from '@/common/components/Form/V2FormSelect/V2FormSelect';
import V2FormDatePicker from '@/common/components/Form/V2FormDatePicker/V2FormDatePicker';

/**
 * 拓店任务-部分字段编辑
 * @Description 拓店任务：直营
 */
const CircleTaskCreateDrawer: FC<{
  /** 提交表单后的回调 */
  onRefresh?: ()=>void,
  ref?: any,
}> = forwardRef(({
  onRefresh,
}, ref) => {

  useImperativeHandle(ref, () => ({
    init: methods.init,
  }));

  const [taskId, setTaskId] = useState<any>(null);
  const [form] = Form.useForm();
  const [requesting, setRequesting] = useState(false);
  const [selection, setSelection] = useState<{
    /** 紧急程度 */
    emergencyOptions: any[],
    /** 踩点紧急度 */
    emergencyDegreeBySpotSurvey: any[],
    /** 备选点上传紧急度 */
    emergencyDegreeByChancePoint: any[],
   }>({ emergencyOptions: [], emergencyDegreeBySpotSurvey: [], emergencyDegreeByChancePoint: [] });

  const [visible, setVisible] = useState(false);

  const tenantCheckData = useSelector((state: any) => state.common.tenantCheck);
  // 是否卡旺卡租户,0626改为是否拓店任务模版
  const isKwk = useMemo(() => {
    return (tenantCheckData?.taskTemplateCode) === 'clusterB';
  }, [tenantCheckData]);


  const methods = useMethods({

    init(taskId: any) {
      setTaskId(taskId);
      setVisible(true);
      setTimeout(() => {
        methods.getSelection();
        methods.loadData();
      }, 0);
    },

    /** 加载详情信息 */
    loadData() {
      if (!taskId) {
        return;
      }
      getCircleTaskDetail({ id: taskId }).then((res) => {
        form.setFieldsValue({
          name: res.name,
          emergencyDegree: res.emergencyDegree,
          spotSurveyEmergencyDegree: res.spotSurveyEmergencyDegree,
          chancePointEmergencyDegree: res.chancePointEmergencyDegree,
          expectDropInDate: res.expectDropInDate ? dayjs(res.expectDropInDate).format('YYYY-MM-DD') : null,

        });
      });
    },

    /**
     * 获取下拉选项
     */
    getSelection: async () => {
      getStandardTaskSelection().then(res => {
        setSelection(val => ({ ...val,
          emergencyOptions: refactorSelection(res.emergencyDegree),
          emergencyDegreeBySpotSurvey: refactorSelection(res.emergencyDegreeBySpotSurvey),
          emergencyDegreeByChancePoint: refactorSelection(res.emergencyDegreeByChancePoint),
        }));
      });
    },

    /**
     * 提交表单
     */
    onSubmit: async():Promise<void> => {
      form.validateFields().then((values: any) => {
        console.log('values', values);
        // 如果是多级，取最后一级，否则使用当前值
        const _emergencyDegree = Array.isArray(values.emergencyDegree) && values.emergencyDegree.length
          ? values.emergencyDegree[values.emergencyDegree.length - 1]
          : values.emergencyDegree;
        const params: any = {
          id: taskId,
          name: values.name,
          emergencyDegree: _emergencyDegree,
          spotSurveyEmergencyDegree: isKwk ? values?.spotSurveyEmergencyDegree : null,
          chancePointEmergencyDegree: isKwk ? values?.chancePointEmergencyDegree : null,
          expectDropInDate: values.expectDropInDate,
        };
        console.log('params', params);
        setRequesting(true);
        postDirectTaskUpdate(params).then(() => {
          V2Message.success('编辑拓店任务成功');
          setVisible(false);
          onRefresh && onRefresh();
        }, err => {
          setVisible(false);
          console.log('catch 失败', err);
          V2Message.error('编辑拓店任务失败');
        }).finally(() => {
          setRequesting(false);
        });
      }).catch((err) => {
        err && showInvalidFieldMsg(err.errorFields, 1);
      });
    },

    onCancel: () => {
      setVisible(false);
    },

  });

  return (
    <Modal
      title='编辑拓店任务'
      open={visible}
      destroyOnClose
      confirmLoading={requesting}
      bodyStyle={{ padding: 0, }}
      className={styles.simpleEditModal}
      onOk={methods.onSubmit}
      onCancel={methods.onCancel}
      // contentWrapperStyle={{
      //   minWidth: 'auto',
      //   width: '1008px',
      // }}
    >
      <V2Form form={form} className={cs('pt-12 pb-12 pl-24 pr-24')} >
        <Row gutter={24}>
          <Col span={12}>
            <V2FormInput required label='拓店任务名称' name='name' maxLength={50} />
          </Col>
          {isKwk ? <>
            {/* 紧急度、踩点紧急度、备选点上传紧急度 */}
            <Col span={12}>
              <V2FormSelect label='踩点紧急度' required name='spotSurveyEmergencyDegree' options={selection.emergencyDegreeBySpotSurvey} />
            </Col>
            <Col span={12}>
              <V2FormSelect label='备选点上传紧急度' required name='chancePointEmergencyDegree' options={selection.emergencyDegreeByChancePoint} />
            </Col>
          </> : <>
            {/* 紧急度、期望落位时间 */}
            <Col span={12}>
              <V2FormSelect required label='紧急度' name='emergencyDegree' options={selection.emergencyOptions} />
            </Col>
            <Col span={12}>
              <V2FormDatePicker required label='期望落位日期' name='expectDropInDate'
                formItemConfig={{
                  getValueFromEvent: (...[, dateString]) => dateString,
                  getValueProps: (value) => ({ value: value ? dayjs(value, 'YYYY-MM-DD') : undefined })
                }}
              />
            </Col>
          </>}
        </Row>
      </V2Form>
    </Modal>
  );
});
export default CircleTaskCreateDrawer;
